import { BaseLayout } from "@layouts/BaseLayout";
import {
  DirectionsRenderer,
  GoogleMap,
  Marker,
  useJsApiLoader,
} from "@react-google-maps/api";
import { isAuthenticated, numberWithCommas } from "@utils/helpers";
import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";

import { DeveloperPropertyCard } from "@shared/cards/developer-property";
import {
  createUserInterest,
  getAllDeveloperAgents,
  getAllDevelopersProjectProperties,
  getDeveloperSingleProperty,
} from "@services/krentDeveloper";
import { Modal } from "react-bootstrap";
import { Agent } from "@utils/constants";
import { DeveloperProjectsResponse } from "@interfaces/app.interface";
import ImageCarousel from "./image-carousel";
import Offers from "./Property/offers";
import { toast, ToastContainer } from "react-toastify";
import {
  collection,
  doc,
  serverTimestamp,
  setDoc,
  Timestamp,
} from "firebase/firestore";
import { firestore } from "@services/firebase.service";
import { useSessionStorage } from "../../../hooks";

interface LocationState {
  fromAgentPage?: boolean;
  shownInterest?: boolean;
}

const DevAccountProjectProperty = () => {
  const { state } = useLocation() as { state: LocationState };
  const navigate = useNavigate();
  const [center, setCenter] = useState({ lat: 9.082, lng: 8.6753 });
  const [directionsResponse, setDirectionsResponse] = useState<any>(null);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [otherProperties, setOtherProperties] = useState<any[]>([]);
  const [showAgentModal, setShowAgentModal] = useState(false);
  const [allAgents, setAllAgents] = useState([]);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const { id } = useParams<{ id: string }>();
  const [property, setProperty] = useState<DeveloperProjectsResponse | any>("");
  const [searchTerm, setSearchTerm] = useState("");
  const [user] = useSessionStorage("user", {});
  const [loadingAgentId, setLoadingAgentId] = useState<string | null>(null);
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env["REACT_APP_GOOGLE_API_KEY"] || "",
    libraries: ["places"],
  });
  const location = useLocation();
  const fromAgentPage =
    (location.state as LocationState)?.fromAgentPage ?? false;
  const messageCollections: any = process.env["REACT_APP_FIREBASE_COLLECTION"];
  const [loadingAgents, setLoadingAgents] = useState(false);

  const shownInterest = (state as any)?.shownInterest;
  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;

    const fetchPropertyDetails = async () => {
      try {
        if (id) {
          const response = await getDeveloperSingleProperty(id, signal);

          setProperty(response.data as any);
        }
      } catch (err) {
        console.error("Failed to fetch propert details:", err);
      }
    };

    fetchPropertyDetails();

    return () => {
      controller.abort();
    };
  }, [id]);

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;

    const fetchOtherProjectProperties = async () => {
      try {
        if (property?.project) {
          const response = await getAllDevelopersProjectProperties(
            property?.project._id,
            signal,
          );
          const filteredProperties = (response.data as any[]).filter(
            (p) => p.id !== property.id,
          );
          setOtherProperties(filteredProperties || []);
        }
      } catch (err) {
        console.error("Failed to fetch project properties:", err);
      }
    };

    fetchOtherProjectProperties();

    return () => {
      controller.abort();
    };
  }, []);
  useEffect(() => {
    if (property) {
      setCenter({
        lat: property.location?.coordinates[1] || 9.082,
        lng: property.location?.coordinates[0] || 8.6753,
      });
      setDirectionsResponse(property);
    }
  }, [property]);

  useEffect(() => {
    if (showAgentModal) {
      const controller = new AbortController();
      const signal = controller.signal;

      const fetchAgents = async () => {
        try {
          setLoadingAgents(true);
          const response = await getAllDeveloperAgents(
            { address: property?.address },
            signal,
          );
          setAllAgents(response.data || []);
        } catch (error) {
          console.error("Failed to fetch agents:", error);
        } finally {
          setLoadingAgents(false);
        }
      };

      fetchAgents();

      return () => {
        controller.abort();
      };
    }
  }, [showAgentModal, property?.address]);

  const handleSendInterest = async (agentId?: string) => {
    if (isAuthenticated()) {
      setLoadingAgentId(agentId || "");
      try {
        const payload = {
          propertyId: id,
          agentId,
        };

        const response = await createUserInterest(payload);

        if (response.success) {
          const interestId = response?.data?._id;

          await setDoc(
            doc(collection(firestore, messageCollections), interestId),
            {
              meta: {
                request: {
                  id: interestId,
                  property: id,
                  request_type: "interest",
                  source: "developer",
                },
                sent_by: {
                  name:
                    user?.prefferredName ||
                    `${user?.firstname} ${user?.lastname}`,
                  photoUrl: user?.photo,
                },
                owner: {
                  name:
                    response.data.agent.firstname +
                    " " +
                    response.data.agent.lastname,
                },
              },
              chats: [
                {
                  from_uid: user?.id,
                  to_uid: response.data.agent.id,
                  type: "text",
                  value: "I'm interested in this developer property",
                  createdAt: Timestamp.now(),
                },
              ],
              read: false,
              members: [response.data.agent.id, user?.id],
              createdAt: serverTimestamp(),
            },
          );

          toast.success("Request sent successfully!");
          setShowAgentModal(false);

          setTimeout(() => {
            navigate(`/purchases`);
          }, 3000);
        }
      } catch (error) {
        console.error("Failed to send interest:", error);
      } finally {
        setLoadingAgentId(null);
      }
    } else {
      localStorage.setItem("InterestIdReroute", JSON.stringify(id));
      localStorage.setItem("agentId", agentId || "");

      setShowLoginModal(true);
    }
  };

  const filteredAgents = allAgents.filter((agent: Agent) =>
    `${agent.firstname} ${agent.lastname}`
      .toLowerCase()
      .includes(searchTerm.toLowerCase()),
  );

  if (!property) {
    return <div>No property details found</div>;
  }

  if (!isLoaded) {
    return <div>Loading...</div>;
  }
  return (
    <BaseLayout>
      <ToastContainer limit={1} />

      <div className="w-80 mx-auto my-10">
        <div className="mt-7">
          <h6 className="my-4">Property details</h6>
          <div className="flex justify-between items-center">
            <div className="flex gap-2 items-center">
              <i className="fa-solid fa-angle-left "></i>
              <a
                href={`/developer-projects/${property.project._id}`}
                className="text-link-secondary fs-16"
              >
                Back to project
              </a>
            </div>
            {((user && Object.keys(user).length === 0) ||
              (user && user.role === "user")) &&
              (fromAgentPage || !shownInterest) &&
              (property.availableUnits > 0 ? (
                <div className="project-details gap-3 items-center">
                  <button
                    onClick={() => setShowAgentModal(true)}
                    className="flex gap-2 items-center bg-primary px-4 py-3 rounded-10 primary-border"
                  >
                    <i className="fa-regular fa-thumbs-up fa-xl text-white"></i>
                    <span className="text-white">Book Now</span>
                  </button>
                </div>
              ) : (
                <div className="bg-red w-max p-2 px-4 text-white rounded-10 fw-bold">
                  <p>Sold out</p>
                </div>
              ))}

            {/* {(user.role !== "agent" ||
              user.role !== "landlord" ||
              user.role !== "developer") &&
              (property.availableUnits > 0 ? (
                !shownInterest &&
                (fromAgentPage ? (
                  ""
                ) : (
                  <div className="project-details gap-3 items-center">
                    <button
                      onClick={() => setShowAgentModal(true)}
                      className="flex gap-2 items-center bg-primary  px-4 py-3 rounded-10 primary-border"
                    >
                      <i className="fa-regular fa-thumbs-up fa-xl  text-white"></i>
                      <span className="text-white ">I am interested</span>
                    </button>
                 
                  </div>
                ))
              ) : (
                <div className="bg-red w-max p-2 px-4 text-white rounded-10 fw-bold ">
                  <p>Sold out</p>
                </div>
              ))} */}
          </div>
        </div>
        <div className="flexed-item my-10 gap-20 items-start">
          <ImageCarousel images={property.otherImages} />

          <div className="w-full">
            <h5
              className={`fw-bold w-max rounded-50 px-3 fs-12 mb-3 ${
                property.propertyDeedType === "shortlet"
                  ? "previous"
                  : property.propertyDeedType === "rent"
                    ? "current"
                    : "future"
              }`}
            >
              <span className="capitalize">{property.propertyDeedType}</span>
            </h5>
            <h6 className="uppercase bg-primary-light w-max rounded-2 p-2 fs-12">
              {property.propertyType}
            </h6>
            <h4 className="my-3">{property.propertyName}</h4>
            <h4 className="my-3">
              <span className="uppercase">{property.currency}</span>&nbsp;
              {numberWithCommas(property.price)}
            </h4>

            <p className="gap-2 flex items-center my-3">
              <i className="fa fa-location-dot text-primary fa-regular"></i>
              {property.address}
            </p>

            <div className="my-3">
              <h6 className="my-2">Property description</h6>
              <p style={{ width: "80%" }}>{property.propertyDescription}</p>
            </div>
            <div className="flex gap-20 items-center my-2">
              <div>
                <span className="fs-14">
                  <i className="fa-light fa-buildings text-primary"></i>&nbsp;{" "}
                  {property.numberOfUnits}&nbsp; Total units
                </span>
              </div>
            </div>
            <div className="flex gap-20 items-center my-2">
              <div>
                <span className="fs-14">
                  <i className="fa-light fa-bed-front text-primary"></i>&nbsp;{" "}
                  {property.numberOfBedrooms} &nbsp; bedrooms
                </span>
              </div>
              <div>
                <span className="fs-14">
                  <i className="fa-light fa-bath text-primary"></i>&nbsp;
                  {property.numberOfBathrooms}&nbsp; bathrooms
                </span>
              </div>
            </div>
          </div>
        </div>

        <Offers property={property} />

        <div className="mt-5">
          <div className="my-4">
            <h5 className="">Map</h5>
            <h6 className="my-2">The precise location of this property</h6>

            <p className="gap-2 flex items-center my-2 text-primary">
              <i className="fa fa-location-dot text-primary fa-regular"></i>
              {property.address} {property.city}, {property.country}
            </p>
          </div>
          <div style={{ height: "25rem", position: "relative" }}>
            <GoogleMap
              center={center}
              zoom={15}
              mapContainerStyle={{
                width: "100%",
                height: "100%",
                borderRadius: "20px",
              }}
              onLoad={setMap}
            >
              <Marker position={center} />
              {directionsResponse && (
                <DirectionsRenderer directions={directionsResponse} />
              )}
            </GoogleMap>

            <div
              className="me-auto bg-white rounded-circle d-flex align-items-center justify-content-center social-icon mb-2 cursor-pointer"
              style={{
                position: "absolute",
                left: "30px",
                bottom: "30px",
              }}
              onClick={() => {
                map?.panTo(center);
                map?.setZoom(15);
              }}
            >
              <i className="fa fa-location-arrow fs-22 text-primary"></i>
            </div>
          </div>
        </div>
        {!shownInterest && (
          <div className="my-5">
            <div className="flex justify-between mb-4">
              <h5>Other Properties in this project</h5>
            </div>
            {otherProperties.length < 1 ? (
              <div className="text-center fs-18">
                No other property in this project
              </div>
            ) : (
              <div className="projects-div">
                {otherProperties.map((p, idx) => (
                  <div key={idx}>
                    <DeveloperPropertyCard property={p} client />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <Modal
          show={showAgentModal}
          onClose={() => setShowAgentModal(false)}
          // size={'md'}
          position="top"
        >
          <div className="row px-4 py-3  ">
            <div className="flex justify-between items-center py-4">
              <h5 className="fw-bolder mb-2">Choose a Krent Agent</h5>
              <div className="flex gap-3 items-center">
                {/* <button
                  className="fs-16 px-4 rounded-10 primary-border text-primary bg-white"
                  onClick={() => handleSendInterest()}
                >
                  SKIP
                </button> */}
                <button
                  onClick={() => setShowAgentModal(false)}
                  className="bg-white"
                >
                  <i className="fa-solid fa-x "></i>
                </button>
              </div>
            </div>

            <div className="searchContainer">
              <i className="fa-regular fa-search searchIcon"></i>

              <input
                className="searchInput"
                placeholder="Search agent name"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="h-40 overflow-scroll mt-4">
              {loadingAgents ? (
                <div className="text-center my-5">
                  <span className="spinner-border text-primary"></span>
                </div>
              ) : filteredAgents.length > 0 ? (
                filteredAgents.map((agent: Agent, idx) => (
                  <div
                    className="flex justify-between my-4 bottom-tab-menu pb-4"
                    key={idx}
                  >
                    <div>
                      <h5>
                        {agent?.firstname.toLowerCase()}{" "}
                        {agent?.lastname.toLowerCase()}
                      </h5>
                      <p>{agent?.email}</p>
                    </div>
                    {agent._id && (
                      <button
                        onClick={() => handleSendInterest(agent?._id || "")}
                        className="flex gap-2 items-center bg-primary text-white px-4 py-2 rounded-10 primary-border fs-16"
                        disabled={loadingAgentId === agent._id}
                      >
                        {loadingAgentId === agent._id ? (
                          <span className="spinner-border spinner-border-sm"></span>
                        ) : (
                          <span className="text-white fw-500">
                            Send interest
                          </span>
                        )}
                      </button>
                    )}
                  </div>
                ))
              ) : (
                <div className="text-center fs-16 my-4">No agents found</div>
              )}
            </div>
          </div>
        </Modal>
        <Modal show={showLoginModal} onClose={() => setShowLoginModal(false)}>
          <div className="row px-4 py-3">
            <div className="my-4">
              <button
                onClick={() => setShowLoginModal(false)}
                className="bg-white float-right"
              >
                <i className="fa-solid fa-x "></i>
              </button>
            </div>
            <h5 className="fw-bolder mb-2">
              <i className="fa-regular fa-lock "></i> Log In to Continue
            </h5>
            <p className="fs-16 my-4">
              To continue, you need to create an account or login to an existing
              account
            </p>
            <div className="d-flex gap-3 justify-content-between mb-3">
              <Link
                to={{ pathname: "/get-started/", search: "utm_source=modal" }}
                className="btn btn-outline-dark rounded-pill py-2 px-2 fw-bold w-full"
              >
                Create your account
              </Link>
              <Link
                to={{ pathname: "/sign-in/", search: "utm_source=modal" }}
                className="btn btn-primary rounded-pill py-2 px-2 w-full"
              >
                Log in to your account
              </Link>
            </div>
          </div>
        </Modal>
      </div>
    </BaseLayout>
  );
};

export default DevAccountProjectProperty;
